import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { CheckCircle2, Package, Loader2 } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!order);

  useEffect(() => {
    if (!order) api.get(`/orders/${id}`).then(({ data }) => setOrder(data.order)).catch(() => {}).finally(() => setLoading(false));
  }, [id, order]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="container-x py-12 max-w-2xl">
      <div className="text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 grid place-items-center mb-4">
          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 font-display">
          {order?.paymentMethod === "COD"
            ? order?.paymentStatus === "PAID" ? "COD Payment Recorded!" : "COD Order Placed!"
            : order?.paymentStatus === "PAID" ? "Payment Successful!" : "Order Placed"}
        </h1>
        <p className="text-slate-500 mt-2">
          {order?.paymentMethod === "COD"
            ? order?.paymentStatus === "PAID"
              ? "Thank you. Cash collection for your delivered order has been recorded."
              : "Thank you. Please pay the amount due in cash when your order is delivered."
            : order?.paymentStatus === "PAID"
              ? "Thank you. Your payment is confirmed and your order will be processed shortly."
              : "Your order has been received and is awaiting payment confirmation."}
        </p>
      </div>

      {order && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-testid="order-success-card">
          <div className="flex items-center justify-between pb-4 border-b border-slate-200">
            <div>
              <p className="text-xs text-slate-500">Order Number</p>
              <p className="font-mono font-bold text-lg text-slate-900" data-testid="order-number">{order.orderNumber}</p>
            </div>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">{order.status?.replace(/_/g, " ")}</span>
          </div>
          <div className="space-y-2 py-4">
            {order.items?.map((it) => (
              <div key={it.id} className="flex items-center gap-3 text-sm">
                <img src={resolveMediaUrl(it.image)} alt="" onError={handleMediaError} className="h-12 w-12 rounded bg-slate-50 object-contain" />
                <span className="flex-1 text-slate-700">{it.name} × {it.quantity}</span>
                <span className="font-mono">{formatINR(it.lineTotal)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 pt-4 space-y-1.5 text-sm">
            <Row label="Subtotal" value={formatINR(order.subtotal)} />
            {order.discount > 0 && <Row label={`Discount${order.discountLabel ? ` — ${order.discountLabel}` : ""}${order.discountSource === "auto" ? " (automatic)" : ""}`} value={`− ${formatINR(order.discount)}`} />}
            <Row label="Shipping" value={order.shippingCharge === 0 ? "Free" : formatINR(order.shippingCharge)} />
            <div className="flex justify-between font-semibold text-slate-900 pt-1"><span>Total</span><span className="font-mono">{formatINR(order.total)}</span></div>
            <Row label="Payment method" value={order.paymentMethod === "COD" ? "Cash on Delivery" : "Razorpay"} />
            <Row label="Payment status" value={order.paymentStatus || "PENDING"} />
            {order.paymentMethod === "COD" && order.paymentStatus !== "PAID" &&
              <Row label="Due on delivery" value={formatINR(order.amountDue ?? order.codAmountDue ?? order.total)} />}
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-3 justify-center">
        <Link to="/track-order" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-5 py-3 rounded-lg" data-testid="track-order-link"><Package className="h-4 w-4" /> Track Order</Link>
        <Link to="/shop" className="inline-flex items-center gap-2 border border-slate-200 font-semibold px-5 py-3 rounded-lg">Continue Shopping</Link>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-mono text-slate-700">{value}</span></div>
);
